import { createZodDto } from 'nestjs-zod/dto';
import { z } from 'zod';
import { dateParser, generalUnitPrice, intervalCount, optionalPositiveInt, optionalPositiveNumber, positiveInt, positiveNumber, storageTbUnitPrice, } from '#validation';
export const optionalString = z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .optional();
export const MAX_TRIAL_LENGTH_IN_YEARS = 2;
export const MAX_TRIAL_LENGTH_IN_DAYS = MAX_TRIAL_LENGTH_IN_YEARS * 365;
const enterpriseTrialPlanLimitsSchema = z.object({
    maxNumberOfMembers: optionalPositiveInt,
    maxNumberOfFilespaces: optionalPositiveInt,
    maxStorageInTb: optionalPositiveNumber,
    maxGuestLinks: optionalPositiveNumber,
    maxEmailsPerGuestLink: optionalPositiveNumber,
    allowCustomStorage: z.boolean().optional(),
    allowOrgInvite: z.boolean().optional(),
    allowServiceAccounts: z.boolean().optional(),
    allowTeamCache: z.boolean().optional(),
    allowNativeLayout: z.boolean().optional(),
});
export const commonEnterpriseTrialSchema = z
    .object({
    trialDays: positiveInt.max(MAX_TRIAL_LENGTH_IN_DAYS),
})
    .and(enterpriseTrialPlanLimitsSchema);
export const DAYS_UNTIL_DUE_OPTIONS = [30, 45, 60, 90];
export const daysUntilDueSchema = z
    .union(DAYS_UNTIL_DUE_OPTIONS.map((option) => z.coerce.number().pipe(z.literal(option))))
    .optional()
    .default(DAYS_UNTIL_DUE_OPTIONS[0]);
const zeroAmountSchema = z
    .object({
    amount: z.literal(0).or(z.literal('0')).or(z.literal('')).optional(),
})
    .transform(() => undefined);
export const prepaidMembersSchema = z.object({
    amount: positiveInt,
    price: generalUnitPrice,
});
export const prepaidStorageSchema = z.object({
    amount: positiveNumber,
    price: storageTbUnitPrice,
});
export const overageStorageSchema = z.object({
    price: storageTbUnitPrice,
});
export const overageStorageProductSchema = z.object({
    overage: overageStorageSchema,
});
export const storageProductSchema = z.object({
    prepaid: zeroAmountSchema.or(prepaidStorageSchema).optional(),
    overage: overageStorageSchema,
});
export const membersProductSchema = z.object({
    prepaid: zeroAmountSchema.or(prepaidMembersSchema).optional(),
    overage: z.object({ price: generalUnitPrice }),
});
export const teamCacheProductSchema = z.object({
    installations: positiveInt,
    pricePerInstallation: generalUnitPrice,
});
export const platformFeeProductSchema = z.object({
    price: generalUnitPrice,
});
export const newMultiMonthlySubscriptionSchema = z
    .object({
    type: z.literal('multi-monthly'),
    contractId: optionalString,
    contractStartDate: dateParser().optional(),
    intervalCount: intervalCount,
    instalmentsCount: optionalPositiveInt,
    skipFirstInstalments: z.coerce.number().int().min(0).optional(),
    serviceAccounts: z.object().optional(),
    orgInvite: z.object().optional(),
    members: membersProductSchema,
    awsStorage: storageProductSchema,
    customStorage: storageProductSchema.optional(),
    legacyProviders: z
        .object({
        wasabiStorage: storageProductSchema,
        ibmStorage: storageProductSchema,
    })
        .optional(),
    nativeLayout: storageProductSchema.optional(),
    teamCache: teamCacheProductSchema.optional(),
    platformFee: platformFeeProductSchema.optional(),
})
    .refine((data) => {
    if (data.instalmentsCount !== undefined) {
        const remainder = data.intervalCount % data.instalmentsCount;
        return remainder === 0;
    }
    return true;
}, {
    message: 'billing.instalments.invalid',
});
export const newEnterprisePlanInvoiceSettingsSchema = z.object({
    daysUntilDue: daysUntilDueSchema,
    disableProrations: z.coerce.boolean().optional(),
    poNumber: z.string().optional(),
});
// An empty string is meaningful here and must be preserved: it instructs the
// backend to clear the corresponding value in Stripe, whereas an absent field
// leaves the current Stripe value unchanged.
export const billingCustomerAddressSchema = z.object({
    street: optionalString,
    city: optionalString,
    state: optionalString,
    postalCode: optionalString,
    country: optionalString,
});
const billingCustomerSchema = z.object({
    billingEmail: z.email().optional(),
    billingAddress: billingCustomerAddressSchema.optional(),
    companyName: z.string().optional(),
});
export const updateBillingCustomerSchema = z.object({
    billingEmail: z.email(),
    billingAddress: billingCustomerAddressSchema.optional(),
    companyName: z.string().optional(),
});
export class BillingCustomerResponseDto extends createZodDto(billingCustomerSchema) {
}
export const newEnterprisePlanLimitsSchema = z.object({
    maxMembers: optionalPositiveInt,
    maxFilespaces: optionalPositiveInt,
    maxStorageTb: optionalPositiveNumber,
    maxGuestLinks: optionalPositiveNumber,
    maxEmailsPerGuestLink: optionalPositiveNumber,
});
